import { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Eye, EyeOff, Loader2, Calendar, Shield, UserCheck, Award, Star, Heart, Target, Zap, LayoutGrid } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const AVAILABLE_ICONS = [
  { value: 'Calendar', label: 'Calendar', icon: Calendar },
  { value: 'Shield', label: 'Shield', icon: Shield },
  { value: 'UserCheck', label: 'User Check', icon: UserCheck },
  { value: 'Award', label: 'Award', icon: Award },
  { value: 'Star', label: 'Star', icon: Star },
  { value: 'Heart', label: 'Heart', icon: Heart },
  { value: 'Target', label: 'Target', icon: Target },
  { value: 'Zap', label: 'Zap', icon: Zap },
];

const iconMap = {
  Calendar, Shield, UserCheck, Award, Star, Heart, Target, Zap
};

import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const WhyOwazaEditor = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [message, setMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCard, setNewCard] = useState({
    title: '',
    description: '',
    icon: 'Shield',
    order: 0,
    visible: true
  });

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/why-owaza`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCards(data);
      }
    } catch (error) {
      console.error('Failed to load cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newCard.title.trim() || !newCard.description.trim()) {
      setMessage('Please fill in title and description');
      return;
    }

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/why-owaza`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newCard,
          order: cards.length + 1
        })
      });

      if (response.ok) {
        setMessage('Card added!');
        setNewCard({ title: '', description: '', icon: 'Shield', order: 0, visible: true });
        setShowAddForm(false);
        fetchCards();
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to create');
      }
    } catch (error) {
      setMessage('Failed to create card');
    }
  };

  const handleUpdate = async (cardId) => {
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/why-owaza/${cardId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        setMessage('Updated successfully!');
        setEditingId(null);
        setEditData({});
        fetchCards();
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      setMessage('Failed to update card');
    }
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/why-owaza/${cardId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage('Deleted successfully!');
        setCards(cards.filter(c => c.id !== cardId));
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      setMessage('Failed to delete card');
    }
  };

  const toggleVisibility = async (card) => {
    const newVisibility = !card.visible;
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/why-owaza/${card.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ visible: newVisibility })
      });

      if (response.ok) {
        setCards(cards.map(c =>
          c.id === card.id ? { ...c, visible: newVisibility } : c
        ));
      }
    } catch (error) {
      console.error('Failed to toggle visibility');
    }
  };

  const startEdit = (card) => {
    setEditingId(card.id);
    setEditData({
      title: card.title,
      description: card.description,
      icon: card.icon,
      order: card.order,
      visible: card.visible
    });
  };

  const renderIconPreview = (iconName, className = "h-5 w-5") => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className={className} /> : null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Why Choose Owaza"
        description="Manage the feature cards in the 'Why Choose Owaza' section"
        icon={<LayoutGrid className="h-6 w-6" />}
        actions={
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            variant="adminPrimary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Card
          </Button>
        }
      />

      {/* Add Form */}
      {showAddForm && (
        <div className="admin-card admin-corner-accent border-2 border-stone-200 rounded-lg p-6 bg-gradient-to-r from-stone-50 to-amber-50/30 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-800 dark:text-gray-200 font-medium">Title</Label>
              <Input
                value={newCard.title}
                onChange={(e) => setNewCard({ ...newCard, title: e.target.value })}
                placeholder="e.g., Self-Defense Training"
                className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
              />
            </div>
            <div>
              <Label className="text-gray-800 dark:text-gray-200 font-medium">Icon</Label>
              <Select value={newCard.icon} onValueChange={(value) => setNewCard({ ...newCard, icon: value })}>
                <SelectTrigger className="border-stone-300 focus:border-amber-500 bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_ICONS.map(({ value, label, icon: Icon }) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span>{label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-gray-800 dark:text-gray-200 font-medium">Description</Label>
            <Textarea
              value={newCard.description}
              onChange={(e) => setNewCard({ ...newCard, description: e.target.value })}
              placeholder="Describe this feature..."
              rows={2}
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} variant="adminPrimary">
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button onClick={() => setShowAddForm(false)} variant="adminOutline">
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.includes('!') && !message.includes('Failed')
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.length === 0 ? (
          <div className="col-span-2 text-center py-12 bg-stone-50 rounded-lg border border-stone-200">
            <p className="text-stone-600 dark:text-stone-400">No cards yet</p>
            <p className="text-sm text-stone-500 dark:text-stone-400">Click "Add Card" to create one</p>
          </div>
        ) : (
          cards.map((card) => (
            <Card key={card.id} className={`admin-card border-stone-200 p-4 ${!card.visible ? 'opacity-60' : ''}`}>
              {editingId === card.id ? (
                // Edit Mode
                <div className="space-y-3">
                  <div>
                    <Label className="text-gray-800 dark:text-gray-200 font-medium">Title</Label>
                    <Input
                      value={editData.title}
                      onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                      className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-800 dark:text-gray-200 font-medium">Icon</Label>
                    <Select value={editData.icon} onValueChange={(value) => setEditData({ ...editData, icon: value })}>
                      <SelectTrigger className="border-stone-300 focus:border-amber-500 bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_ICONS.map(({ value, label, icon: Icon }) => (
                          <SelectItem key={value} value={value}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              <span>{label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-800 dark:text-gray-200 font-medium">Description</Label>
                    <Textarea
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      rows={2}
                      className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-800 dark:text-gray-200 font-medium">Order</Label>
                    <Input
                      type="number"
                      value={editData.order}
                      onChange={(e) => setEditData({ ...editData, order: parseInt(e.target.value) })}
                      className="w-24 border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                    />
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-stone-200">
                    <Button onClick={() => handleUpdate(card.id)} variant="adminPrimary" size="sm">
                      <Save className="h-3 w-3 mr-1" />
                      Save
                    </Button>
                    <Button onClick={() => { setEditingId(null); setEditData({}); }} variant="adminOutline" size="sm">
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        {renderIconPreview(card.icon, "h-5 w-5 text-red-600")}
                      </div>
                      <div>
                        <h4 className={`font-semibold ${card.visible ? 'text-gray-900 dark:text-gray-100' : 'text-stone-400'}`}>
                          {card.title}
                        </h4>
                        <span className="text-xs text-stone-500 dark:text-stone-400">Order: {card.order}</span>
                      </div>
                    </div>
                    {!card.visible && (
                      <span className="text-xs bg-stone-200 text-stone-600 dark:text-stone-400 px-2 py-0.5 rounded">Hidden</span>
                    )}
                  </div>
                  <p className={`text-sm mb-4 ${card.visible ? 'text-stone-600 dark:text-stone-400' : 'text-stone-400'}`}>
                    {card.description}
                  </p>
                  <div className="flex items-center gap-2 pt-3 border-t border-stone-200">
                    <Button
                      onClick={() => toggleVisibility(card)}
                      variant="ghost"
                      size="sm"
                      className="text-stone-600 dark:text-stone-400 hover:text-amber-600"
                    >
                      {card.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button
                      onClick={() => startEdit(card)}
                      variant="ghost"
                      size="sm"
                      className="text-stone-600 dark:text-stone-400 hover:text-amber-600"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(card.id)}
                      variant="ghost"
                      size="sm"
                      className="text-stone-600 dark:text-stone-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default WhyOwazaEditor;
